/**
 * African Greens cart-meter "unlock" celebration (AQL).
 *
 * Drives the one-time confetti burst + congrats pill for the free-shipping
 * truck (snippets/ag-freeship-bar.liquid) and the two tier milestone
 * markers (snippets/ag-tier-nudge.liquid). Lives as its own theme asset,
 * included once from layout/theme.liquid, rather than a {% javascript %}
 * block in either snippet: a {% javascript %} block only ships in the
 * compiled snippet-scripts bundle when that snippet actually rendered on
 * the initial page load, so on a product page with the cart drawer closed
 * the drawer's copy of the meters (and this script) never got a chance to
 * load, and the mobile drawer never fired.
 *
 * Plain script (no import/export), idempotent: guarded so it only sets up
 * its listeners once even if the asset tag somehow ends up on the page more
 * than once.
 */
(function () {
  if (window.__agCartBurstLoaded) return;
  window.__agCartBurstLoaded = true;

  console.debug('ag-burst', 'loaded');

  var FS_STORAGE_KEY = 'ag-fs-celebrated'; // '1' once celebrated, absent otherwise
  var TIER_STORAGE_KEY = 'ag-tier-celebrated'; // highest tier level celebrated: '0' / '1' / '2'

  var CONFETTI_COLORS = ['#011919', '#1f4d3a', '#3f7d5c', '#8fbf9f', '#c9a96e'];
  var POP_KEYFRAMES = [{ transform: 'scale(1)' }, { transform: 'scale(1.2)' }, { transform: 'scale(1)' }];

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  // Re-entrancy guard: boot() can run many times in quick succession
  // (cart:update, dialog:open, the debounced body observer), and an
  // attempt can be mid-poll (waiting on the drawer) when another trigger
  // fires. Keyed on whichever element represents "this attempt" (an icon
  // for free shipping, a track for the tier bar, since a tier attempt can
  // cover two markers at once). This only prevents STARTING a second
  // attempt; nothing here ever cancels one already in flight, so a
  // re-render arriving mid-attempt can't strand anything.
  var polling = new WeakSet();

  function pointRelativeTo(el, dialog) {
    var rect = el.getBoundingClientRect();
    if (dialog) {
      var dialogRect = dialog.getBoundingClientRect();
      return { x: rect.left - dialogRect.left + rect.width / 2, y: rect.top - dialogRect.top + rect.height / 2 };
    }
    return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
  }

  // True once the message's rect actually intersects the real browser
  // viewport by at least 8px. A celebration only ever fires when this is
  // true: on the /cart page a line item split can push the meter 250px+
  // below the fold, and there is no sane place to put a pill or a confetti
  // origin for a message the customer cannot see, so those crossings are
  // skipped rather than falling back to some other position that would
  // land on unrelated page content. Takes an already-measured rect, not an
  // element, so the zero-height ("not laid out yet") case can be told apart
  // from a genuinely off-screen one by whoever calls this (see
  // celebrateIfVisible below).
  function isMessageVisible(rect) {
    var visibleHeight = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
    return rect.bottom > 0 && rect.top < window.innerHeight && visibleHeight >= 8;
  }

  // Places an already-appended (and therefore measurable) pill directly OVER
  // the meter's own message line, rather than floating above it: same left
  // edge as the meter, vertically centred on the message's own box, so the
  // only thing it covers is the sentence it is effectively replacing (which
  // says the same thing), not whatever content sits above the meter. Clamped
  // inside the layer's own box with 8px side margins so it can't run past
  // the drawer/page edges, and clamped vertically against the REAL browser
  // viewport (not just the dialog) with an 8px margin top and bottom. Only
  // ever called when isMessageVisible() has already confirmed the message
  // itself is on screen, so there is no off-screen fallback case to handle.
  function placeCongratsPill(pill, layer, dialog, messageEl) {
    var pillW = pill.offsetWidth;
    var pillH = pill.offsetHeight;
    var base = dialog ? dialog.getBoundingClientRect() : { left: 0, top: 0 };
    var msgRect = messageEl.getBoundingClientRect();
    var layerRect = layer.getBoundingClientRect();

    var idealLeft = msgRect.left;
    var minLeft = layerRect.left + 8;
    var maxLeft = Math.max(minLeft, layerRect.left + layerRect.width - 8 - pillW);
    var left = Math.min(Math.max(idealLeft, minLeft), maxLeft);

    var idealTop = msgRect.top + msgRect.height / 2 - pillH / 2;
    var minTop = 8;
    var maxTop = Math.max(minTop, window.innerHeight - 8 - pillH);
    var top = Math.min(Math.max(idealTop, minTop), maxTop);

    pill.style.left = left - base.left + 'px';
    pill.style.top = top - base.top + 'px';
  }

  // Waits (bounded, 900ms max) for the dialog's own open/slide animations to
  // finish before measuring, so the celebration reads as a "reveal" once
  // the drawer has settled instead of firing while it's still visibly
  // moving. Polls via requestAnimationFrame rather than tracking .finished
  // promises: simpler, and nothing here needs to be cancelled if it runs
  // past its usefulness, it just calls onReady once and stops.
  function pollUntilSettled(dialog, startedAt, onReady) {
    var animations = dialog.getAnimations({ subtree: true });
    if (animations.length === 0 || Date.now() - startedAt >= 900) {
      onReady();
      return;
    }
    requestAnimationFrame(function () {
      pollUntilSettled(dialog, startedAt, onReady);
    });
  }

  // callback(dialogOrNull) once ready to measure; dialogOrNull is the
  // <dialog> ancestor if there is one (already settled), or null on the
  // standalone /cart page.
  function withSettledDialog(anchorEl, callback) {
    var dialog = anchorEl.closest('dialog');
    if (!dialog) {
      callback(null);
      return;
    }
    pollUntilSettled(dialog, Date.now(), function () {
      callback(dialog);
    });
  }

  // ---- clipping layer ----
  // Confetti and the congrats pill render inside a layer that clips to the
  // dialog (or the viewport with no dialog), so falling/spinning pieces can
  // never push out scrollable overflow on the page. Inside a dialog the
  // layer is position:absolute + inset:0, appended as a child of the
  // dialog itself, which both clips correctly (overflow:hidden) and
  // inherits the dialog's top-layer stacking (so it paints above the
  // drawer's own content). On the /cart page it's position:fixed +
  // inset:0 on document.body, covering the viewport. The layer removes
  // itself once everything inside has finished.
  function createClipLayer(dialog) {
    var layer = document.createElement('div');
    layer.setAttribute('aria-hidden', 'true');
    layer.style.position = dialog ? 'absolute' : 'fixed';
    layer.style.inset = '0';
    layer.style.overflow = 'hidden';
    layer.style.pointerEvents = 'none';
    layer.style.zIndex = '2147483647';
    (dialog || document.body).appendChild(layer);
    return layer;
  }

  // ---- confetti ----

  function spawnConfettiPiece(layer, x, y, onDone) {
    var isRect = Math.random() < 0.5;
    var color = CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)];
    var piece = document.createElement('span');
    piece.style.position = 'absolute';
    piece.style.left = x + 'px';
    piece.style.top = y + 'px';
    piece.style.background = color;
    if (isRect) {
      piece.style.width = '10px';
      piece.style.height = '6px';
    } else {
      piece.style.width = '6px';
      piece.style.height = '6px';
      piece.style.borderRadius = '50%';
    }
    layer.appendChild(piece);

    // Launch angle biased upward and outward (screen coords, y-down): -150deg
    // to -30deg is a 120deg cone centred on straight up. A gravity-like curve
    // is approximated with keyframes rather than a physics loop: the piece
    // reaches its outward/upward peak by 40%, then falls further past that
    // peak with a little extra horizontal drift by 100%, opacity held until
    // 70% then fading out over the last 30% as required.
    var angle = (-150 + Math.random() * 120) * (Math.PI / 180);
    var speed = 60 + Math.random() * 70;
    var dx0 = Math.cos(angle) * speed;
    var dy0 = Math.sin(angle) * speed;
    var driftX = (Math.random() - 0.5) * 40;
    var fall = 90 + Math.random() * 90;
    var dx1 = dx0 + driftX * 0.6;
    var dy1 = dy0 + fall * 0.55;
    var dx2 = dx0 + driftX;
    var dy2 = dy0 + fall;
    var spin = (Math.random() < 0.5 ? -1 : 1) * (360 + Math.random() * 360);
    var duration = 1200 + Math.random() * 300;

    function frame(dx, dy, deg, opacity, offset) {
      return {
        transform: 'translate(-50%, -50%) translate(' + dx.toFixed(1) + 'px, ' + dy.toFixed(1) + 'px) rotate(' + deg.toFixed(0) + 'deg)',
        opacity: opacity,
        offset: offset,
      };
    }

    var anim = piece.animate(
      [
        frame(0, 0, 0, 1, 0),
        frame(dx0, dy0, spin * 0.4, 1, 0.4),
        frame(dx1, dy1, spin * 0.7, 1, 0.7),
        frame(dx2, dy2, spin, 0, 1),
      ],
      { duration: duration, easing: 'ease-out', fill: 'forwards' }
    );
    anim.onfinish = function () {
      piece.remove();
      onDone();
    };
  }

  function spawnConfettiBurst(layer, x, y, onAllDone) {
    var remaining = 40;
    for (var i = 0; i < remaining; i++) {
      spawnConfettiPiece(layer, x, y, function () {
        remaining--;
        if (remaining === 0) onAllDone();
      });
    }
  }

  // ---- congrats pill ----

  function spawnCongrats(layer, dialog, messageEl, text, onDone) {
    var pill = document.createElement('div');
    pill.setAttribute('aria-hidden', 'true');
    pill.textContent = text;
    pill.style.position = 'absolute';
    pill.style.background = '#fff';
    pill.style.color = '#011919';
    pill.style.fontSize = '14px';
    pill.style.fontWeight = '600';
    pill.style.borderRadius = '999px';
    pill.style.padding = '8px 14px';
    pill.style.boxShadow = '0 6px 20px rgba(1,25,25,.12)';
    pill.style.whiteSpace = 'nowrap';
    // Placed invisibly first so its rendered size can be measured, then
    // positioned exactly (see placeCongratsPill), before it's ever shown.
    pill.style.visibility = 'hidden';
    layer.appendChild(pill);
    placeCongratsPill(pill, layer, dialog, messageEl);
    pill.style.visibility = '';

    if (reducedMotion()) {
      pill.style.opacity = '1';
      setTimeout(function () {
        pill.remove();
        onDone();
      }, 2000);
      return;
    }

    var anim = pill.animate(
      [
        { opacity: 0, transform: 'translateY(8px)', offset: 0 },
        { opacity: 1, transform: 'translateY(0)', offset: 0.15 },
        { opacity: 1, transform: 'translateY(0)', offset: 0.85 },
        { opacity: 0, transform: 'translateY(-8px)', offset: 1 },
      ],
      { duration: 2000, easing: 'ease-out', fill: 'forwards' }
    );
    anim.onfinish = function () {
      pill.remove();
      onDone();
    };
  }

  // Fires the confetti (skipped under reduced motion) and the congrats pill
  // (always shown, animated or not) into a fresh clip layer, removing the
  // layer once everything inside it is done.
  function celebrate(dialog, confettiPoint, messageEl, message) {
    var layer = createClipLayer(dialog);
    var pendingUnits = 1; // the pill
    function release() {
      pendingUnits--;
      if (pendingUnits <= 0) layer.remove();
    }
    if (!reducedMotion()) {
      pendingUnits++;
      spawnConfettiBurst(layer, confettiPoint.x, confettiPoint.y, release);
    }
    spawnCongrats(layer, dialog, messageEl, message, release);
  }

  // Re-derives the message element fresh via findMessageEl() right at the
  // moment celebration would actually fire (after the dialog-settle poll),
  // rather than using a value read earlier: the message line can still be
  // mid-layout at that exact instant (e.g. a line-item split re-rendering
  // the drawer), which reads as a zero-height rect and is NOT the same as
  // being off-screen. A zero-height rect is retried once after 300ms; a
  // real (non-zero) rect that fails isMessageVisible is treated as
  // genuinely off-screen and is not retried. 'fired' is only logged once
  // celebrate() has actually run (and so the clip layer actually exists).
  function celebrateIfVisible(findMessageEl, icon, dialog, message, attempt) {
    var messageEl = findMessageEl();
    var msgRect = messageEl.getBoundingClientRect();
    if (msgRect.height === 0) {
      if (attempt === 0) {
        setTimeout(function () {
          celebrateIfVisible(findMessageEl, icon, dialog, message, 1);
        }, 300);
        return;
      }
      console.debug('ag-burst', 'not-laid-out');
      return;
    }
    if (!isMessageVisible(msgRect)) {
      console.debug('ag-burst', 'off-screen');
      return;
    }
    var confettiPoint = pointRelativeTo(icon, dialog);
    celebrate(dialog, confettiPoint, messageEl, message);
    console.debug('ag-burst', 'fired');
  }

  // ---- free shipping ----

  function attemptFreeshipCelebration(track, icon) {
    if (polling.has(icon)) return;
    polling.add(icon);
    withSettledDialog(icon, function (dialog) {
      polling.delete(icon);
      var rect = icon.getBoundingClientRect();
      if (rect.width === 0) {
        console.debug('ag-burst', 'skip zero rect');
        return; // not laid out: do not set the flag, a later trigger retries
      }
      sessionStorage.setItem(FS_STORAGE_KEY, '1');
      if (!reducedMotion()) icon.animate(POP_KEYFRAMES, { duration: 350, easing: 'ease-out' });
      celebrateIfVisible(
        function () {
          return track.closest('.ag-fs').querySelector('.ag-fs__msg');
        },
        icon,
        dialog,
        'Congrats, free shipping is yours.',
        0
      );
    });
  }

  function bootFreeship() {
    document.querySelectorAll('.ag-fs__track').forEach(function (track) {
      var icon = track.querySelector('.ag-fs__truck-icon');
      if (!icon) return;
      var pct = parseFloat(track.style.getPropertyValue('--ag-fs-pct')) || 0;
      var reached = pct >= 100;
      var flagged = sessionStorage.getItem(FS_STORAGE_KEY) === '1';
      if (reached && !flagged) {
        attemptFreeshipCelebration(track, icon);
      } else if (!reached && flagged) {
        sessionStorage.removeItem(FS_STORAGE_KEY);
        console.debug('ag-burst', 'cleared');
      }
    });
  }

  // ---- tier milestones ----

  function tierMessageFor(icon) {
    var marker = icon.closest('.ag-tier__marker');
    var n = parseInt(marker && marker.dataset.freeCount, 10);
    if (!n) n = 1;
    var noun = n === 1 ? 'item is' : 'items are';
    return 'Congrats, ' + n + ' ' + noun + ' free.';
  }

  // Waits once for the dialog to settle, then fires a SINGLE celebration for
  // the highest tier level now reached (even if the cart count jumped past
  // both thresholds in one update, e.g. typing a quantity directly): only
  // the highest marker pops and only one confetti burst + pill fires.
  function attemptTierCelebration(track, m1Gift, m2Gift, currentLevel) {
    if (polling.has(track)) return;
    polling.add(track);
    var icon = currentLevel === 2 ? m2Gift : m1Gift;
    withSettledDialog(icon, function (dialog) {
      polling.delete(track);
      var rect = icon.getBoundingClientRect();
      if (rect.width === 0) {
        console.debug('ag-burst', 'skip zero rect');
        return; // not laid out: do not set the flag, a later trigger retries
      }
      sessionStorage.setItem(TIER_STORAGE_KEY, String(currentLevel));
      if (!reducedMotion()) icon.animate(POP_KEYFRAMES, { duration: 350, easing: 'ease-out' });
      celebrateIfVisible(
        function () {
          return track.closest('.ag-tier').querySelector('.ag-tier__msg');
        },
        icon,
        dialog,
        tierMessageFor(icon),
        0
      );
    });
  }

  function bootTier() {
    document.querySelectorAll('.ag-tier__track').forEach(function (track) {
      var m1Gift = track.querySelector('.ag-tier__marker--m1 .ag-tier__gift');
      var m2Gift = track.querySelector('.ag-tier__marker--m2 .ag-tier__gift');
      if (!m1Gift || !m2Gift) return;

      var currentLevel = 0;
      if (m2Gift.classList.contains('is-unlocked')) currentLevel = 2;
      else if (m1Gift.classList.contains('is-unlocked')) currentLevel = 1;

      var stored = parseInt(sessionStorage.getItem(TIER_STORAGE_KEY), 10);
      if (isNaN(stored)) stored = 0;

      if (currentLevel > stored) {
        attemptTierCelebration(track, m1Gift, m2Gift, currentLevel);
      } else if (currentLevel < stored) {
        sessionStorage.setItem(TIER_STORAGE_KEY, String(currentLevel));
        console.debug('ag-burst', 'cleared');
      }
    });
  }

  function boot() {
    bootFreeship();
    bootTier();
  }

  // A single global observer (not one per icon) means it survives any
  // re-render regardless of whether the drawer patches a node in place or
  // swaps it wholesale, and it also catches the drop-below-goal case.
  // Debounced 50ms so one morph's burst of mutation records collapses into
  // a single boot() call. The dialog's own 'dialog:open' event is
  // dispatched on the <cart-drawer-component> wrapper itself and does not
  // bubble, so it's listened for there directly rather than on document.
  var debounceTimer = null;
  function scheduleBoot() {
    if (debounceTimer) return;
    debounceTimer = setTimeout(function () {
      debounceTimer = null;
      boot();
    }, 50);
  }

  function init() {
    boot();
    document.addEventListener('cart:update', boot);
    document.querySelectorAll('cart-drawer-component').forEach(function (el) {
      el.addEventListener('dialog:open', boot);
    });
    new MutationObserver(scheduleBoot).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
